/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { useEffect, useRef } from "react";
import { MapApi } from "client";
import { useApiWithTimeout } from "./ApiWrapperWithTimeout";
import { Const } from "const";
import { useUserContext } from "context/UserContext";
import { useMapDataContext } from "context/MapDataProvider";
import { useCallApi } from "./CallApiHook";

export const useGetMapData = () => {
  const [mapApi, cancelMapApi] = useApiWithTimeout(
    MapApi,
    Const.operationTimeout.getMapInfo
  );
  const { jobsite } = useUserContext();
  const { mapData, mapVer } = useMapDataContext();
  const mapDataRef = useRef(mapData);
  const mapVerRef = useRef(mapVer);
  useEffect(() => {
    if (jobsite?.jobSiteId) {
      mapDataRef.current.mapId = "";
    }
  }, [jobsite]);
  useEffect(() => {
    mapDataRef.current = mapData;
    mapVerRef.current = mapVer;
  }, [mapData, mapVer]);

  return useCallApi(
    async () => {
      if (jobsite?.jobSiteId) {
        const nextMapVer =
          mapDataRef.current.mapId !== ""
            ? await mapApi.getMapUpdate({
                mapId: mapDataRef.current.mapId,
                mapVer: mapDataRef.current.mapVer,
              })
            : { mapVer: 0, necessityOfUpdate: true };

        if (nextMapVer.necessityOfUpdate) {
          // new map version exist.
          const latestMapInfo = await mapApi.getMapInfo({
            mapId: jobsite.mapId,
            jobSiteId: jobsite.jobSiteId,
          });
          return {
            mapInfo: latestMapInfo,
            mapVer: nextMapVer,
          };
        }
      }
      return {
        mapInfo: mapDataRef.current,
        mapVer: mapVerRef.current,
      };
    },
    cancelMapApi,
    [jobsite, mapApi, cancelMapApi]
  );
};
